/**
 * Rule 72(t) / Substantially Equal Periodic Payments (SEPP)
 * calculator — Phase 7 Session 1.
 *
 * **The pattern**: take pre-59½ retirement-account distributions
 * without the 10% early-withdrawal penalty (IRC § 72(t)(1)) by
 * committing to a "substantially equal periodic payments" schedule
 * for at LEAST 5 years OR until age 59½, whichever is later (per IRC
 * § 72(t)(4)). Bust the schedule and the IRS retroactively imposes
 * the 10% penalty on every prior distribution.
 *
 * **Three IRS-approved methods** (Notice 2022-6, effective Jan 2023):
 *
 *   1. **Required Minimum Distribution (RMD) method**
 *      `payment = balance / single_life_expectancy(age)`
 *      Recomputed annually. Smallest payments early; grows with
 *      decreasing life expectancy.
 *
 *   2. **Fixed Amortization method**
 *      Payment is the level annual amount that fully amortizes the
 *      account balance over the single-life-expectancy years at the
 *      chosen interest rate.
 *      `payment = balance × (i × (1+i)^N) / ((1+i)^N - 1)` where
 *        i = interest_rate / 100 and N = single_life_expectancy(age).
 *      Computed ONCE; held constant for the life of the SEPP.
 *
 *   3. **Fixed Annuitization method**
 *      `payment = balance / annuity_factor`
 *      Annuity factor uses an IRS-approved mortality table + the
 *      chosen interest rate. v1 simplification: uses Single Life
 *      Expectancy as N (close-enough; full Mortality Table A → Phase
 *      11 polish). Computed ONCE; held constant.
 *
 * **Interest rate cap** (Notice 2022-6): the maximum rate is the
 * GREATER OF (a) 5%, OR (b) 120% of the federal mid-term rate for
 * either of the two months immediately before distributions begin.
 * Caller passes `irs_max_rate_pct`; helper validates `interest_rate
 * <= irs_max_rate_pct` and rejects with an error otherwise.
 *
 * **Authority**:
 *   - IRC § 72(t)(2)(A)(iv) — SEPP exception to the 10% penalty.
 *   - IRS Notice 2022-6 — current method definitions + rate cap.
 *   - Treas Reg § 1.401(a)(9)-9 (Single Life Expectancy Table 2022).
 *
 * **Web mirror** at `web/src/lib/util/sepp.ts`.
 */

import {
  IRS_SINGLE_LIFE_EXPECTANCY_2022,
  singleLifeExpectancy,
} from "../data/irs-single-life-expectancy-2022.js";

export type SeppMethod = "rmd" | "amortization" | "annuitization";

export interface SeppInput {
  readonly account_balance_cents: number;
  /** Age at the start of the SEPP. Must be ≥ 30 + ≤ 59 (must be < 59½
   *  for SEPP to make sense; 59 is OK if before birthday). */
  readonly current_age: number;
  /** Method per Notice 2022-6. */
  readonly method: SeppMethod;
  /** Interest rate as a percentage (e.g., 5.0 for 5%). Required for
   *  amortization + annuitization; ignored for RMD. */
  readonly interest_rate_pct?: number;
  /** Maximum allowed rate per Notice 2022-6 (greater of 5% or 120% of
   *  federal mid-term rate). Caller looks this up; helper validates. */
  readonly irs_max_rate_pct?: number;
}

export interface SeppResult {
  readonly method: SeppMethod;
  /** First-year annual distribution. RMD method recomputes this each
   *  year; amortization + annuitization hold constant. */
  readonly annual_distribution_cents: number;
  /** Effective payout rate as a % of starting balance. */
  readonly effective_rate_pct: number;
  /** Years until the SEPP can stop (max of 5 years OR age-59½, per
   *  IRC § 72(t)(4)). */
  readonly minimum_years: number;
  /** Single-life-expectancy used. */
  readonly life_expectancy_years: number;
  /** Interest rate used (input passthrough). 0 for RMD method. */
  readonly interest_rate_pct: number;
  /** Sum of the first `minimum_years` distributions assuming constant
   *  payments (amortization/annuitization) OR a simple RMD walk
   *  forward (recomputes via the table each year). */
  readonly minimum_total_distributions_cents: number;
  /** "Bust the SEPP and pay the 10% penalty retroactively" exposure if
   *  the user stops early. = `0.10 × cumulative_distributions_cents`
   *  for any year the user stops in. */
  readonly retroactive_penalty_at_stop_cents: number;
}

export function seppCalculator(input: SeppInput): SeppResult {
  if (!Number.isInteger(input.account_balance_cents)) {
    throw new Error(
      "seppCalculator: account_balance_cents must be integer cents",
    );
  }
  if (input.account_balance_cents <= 0) {
    throw new Error(
      "seppCalculator: account_balance_cents must be positive",
    );
  }
  if (input.current_age < 30 || input.current_age > 59) {
    throw new Error(
      `seppCalculator: current_age ${input.current_age} out of range — SEPP requires < 59.5; the helper supports 30..59`,
    );
  }

  const N = singleLifeExpectancy(input.current_age);

  let interest_rate_pct = 0;
  let annual_distribution: number;

  if (input.method === "rmd") {
    annual_distribution = Math.round(input.account_balance_cents / N);
  } else {
    if (input.interest_rate_pct === undefined) {
      throw new Error(
        `seppCalculator: ${input.method} method requires interest_rate_pct`,
      );
    }
    if (input.interest_rate_pct <= 0) {
      throw new Error(
        `seppCalculator: interest_rate_pct must be positive for ${input.method}`,
      );
    }
    if (
      input.irs_max_rate_pct !== undefined &&
      input.interest_rate_pct > input.irs_max_rate_pct
    ) {
      throw new Error(
        `seppCalculator: interest_rate_pct ${input.interest_rate_pct}% exceeds IRS max ${input.irs_max_rate_pct}% (Notice 2022-6 rate cap)`,
      );
    }
    interest_rate_pct = input.interest_rate_pct;
    const i = input.interest_rate_pct / 100;
    if (input.method === "amortization") {
      // Level payment that amortizes the balance over N years at rate i.
      // payment = balance × i × (1+i)^N / ((1+i)^N - 1)
      const factor = (i * Math.pow(1 + i, N)) / (Math.pow(1 + i, N) - 1);
      annual_distribution = Math.round(input.account_balance_cents * factor);
    } else {
      // Annuitization: balance ÷ annuity_factor where
      // annuity_factor = (1 - (1+i)^-N) / i
      const annuity_factor = (1 - Math.pow(1 + i, -N)) / i;
      annual_distribution = Math.round(
        input.account_balance_cents / annuity_factor,
      );
    }
  }

  // SEPP must run for ≥ 5 years OR until age 59½, whichever is later.
  const yearsUntil59_5 = Math.max(0, 59.5 - input.current_age);
  const minimum_years = Math.max(5, Math.ceil(yearsUntil59_5));

  let minimum_total = 0;
  if (input.method === "rmd") {
    // Walk forward, recomputing each year.
    let balance = input.account_balance_cents;
    for (let y = 0; y < minimum_years; y++) {
      const age_y = input.current_age + y;
      const N_y = age_y <= 100 ? singleLifeExpectancyOrLast(age_y) : 2.0;
      const payment_y = Math.round(balance / N_y);
      minimum_total += payment_y;
      // RMD walk: balance grows by some growth rate (we don't model
      // returns here — Phase 11 polish), then subtracts the payment.
      // For "minimum_total" we just sum the payments at constant
      // balance which is conservative.
      balance -= payment_y;
      if (balance <= 0) break;
    }
  } else {
    minimum_total = annual_distribution * minimum_years;
  }

  const effective_rate_pct =
    Math.round((annual_distribution / input.account_balance_cents) * 10000) /
    100;
  const retroactive_penalty_at_stop_cents = Math.round(
    minimum_total * 0.1,
  );

  return {
    method: input.method,
    annual_distribution_cents: annual_distribution,
    effective_rate_pct,
    minimum_years,
    life_expectancy_years: N,
    interest_rate_pct,
    minimum_total_distributions_cents: minimum_total,
    retroactive_penalty_at_stop_cents,
  };
}

/** Like singleLifeExpectancy, but clamps to age 100 (last row in
 *  IRS_SINGLE_LIFE_EXPECTANCY_2022). Used during the RMD walk-forward
 *  when the SEPP runs into very late ages. */
function singleLifeExpectancyOrLast(age: number): number {
  const last = IRS_SINGLE_LIFE_EXPECTANCY_2022[
    IRS_SINGLE_LIFE_EXPECTANCY_2022.length - 1
  ];
  if (age >= 100) return last?.years_remaining ?? 2.0;
  return singleLifeExpectancy(age);
}
